import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Route, Routes, NavLink } from "react-router-dom";
import HubPage from "./pages/HubPage";
import AssignRolesPage from "./pages/AssignRolesPage";
import CreateTopic from "./pages/CreateTopic";
import ExistingTopics from "./pages/ExistingTopics";
import ClaimRewards from "./pages/ClaimRewards"; // Import the new ClaimRewards component
import StatisticsPage from "./pages/StatisticsPage";
import { connectWallet, getContractThroughJsonProvider, getSignerContract, checkNetwork, switchTo6DegreeNetwork } from "./utils/dappProvider";
import jazzicon from "@metamask/jazzicon";

function App() {
  const [account, setAccount] = useState(null);
  const [contract, setContract] = useState(null);
  const [signerContract, setSignerContract] = useState(null);
  const avatarRef = useRef(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);

  // Check if MetaMask is connected on page load
  useEffect(() => {
    const checkWeb3Wallet = () => {
      if (typeof window.ethereum === 'undefined') {
        setShowProviderModal(true);
      } else {
        // Check network on load
        checkNetwork()
          .then(isCorrectNetwork => {
            if (!isCorrectNetwork) {
              setShowNetworkModal(true);
            }
          })
          .catch(err => console.error(err));
      }
    };

    checkWeb3Wallet();
    const checkIfConnected = async () => {
      if (window.ethereum) {
        const accounts = await window.ethereum.request({ method: "eth_accounts" });
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          generateJazzicon(accounts[0]);
        }
      }
    };

    const initContracts = async () => {
      const contractInstance = getContractThroughJsonProvider();
      setContract(contractInstance);

      const { signer } = await connectWallet();
      const signerContractInstance = getSignerContract(signer);
      setSignerContract(signerContractInstance);
    };

    checkIfConnected();
    initContracts();

    if (window.ethereum) {
      window.ethereum.on("accountsChanged", (accounts) => {
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          generateJazzicon(accounts[0]);
          initContracts(accounts[0]); // Re-initialize contracts with the new account
        } else {
          setAccount(null);
          avatarRef.current.innerHTML = ''; // Clear the avatar if no account is connected
        }
      });
    }
  }, []);

  const handleSwitchNetwork = async () => {
    console.log("trying to switch network");

    await switchTo6DegreeNetwork();
    setShowNetworkModal(false);
  };

  const handleConnectWallet = async () => {

    if (typeof window.ethereum === 'undefined') {
      setShowProviderModal(true);
    }
    else {
      const { address } = await connectWallet();
      setAccount(address);
      generateJazzicon(address);
    }
  };

  const generateJazzicon = (address) => {
    const seed = parseInt(address.slice(2, 10), 16); // Generate a seed from the address
    const icon = jazzicon(30, seed); // Create the Jazzicon with a diameter of 30px
    if (avatarRef.current) {
      avatarRef.current.innerHTML = ''; // Clear any existing icon
      avatarRef.current.appendChild(icon); // Append the new icon to the avatar element
    }
  };

  // Persist Jazzicon when the account is already connected
  useEffect(() => {
    if (account) {
      generateJazzicon(account);
    }
  }, [account]);

  return (
    <div className="App">
      <Router>
        <nav className="navbar navbar-expand-lg navbar-light bg-light sticky-navbar" style={{
          position: 'sticky',
          top: 0,
          zIndex: 1020,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          backgroundColor: 'rgba(248, 249, 250, 1)',
        }}>
          <div className="container-fluid">
            <NavLink className="navbar-brand" to="/" style={{ fontWeight: 'bold', color: '#007bff' }}>
              DuelApp
            </NavLink>
            <button
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarNav"
              aria-controls="navbarNav"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
            <div className="collapse navbar-collapse" id="navbarNav">
              <ul className="navbar-nav me-auto">
                <li className="nav-item">
                  <NavLink className="btn btn-outline-primary mx-1 mb-1" to="/" activeClassName="active">
                    Home
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink className="btn btn-outline-primary mx-1 mb-1" to="/assign-roles" activeClassName="active">
                    Assign Roles
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink className="btn btn-outline-primary mx-1 mb-1" to="/create-topic" activeClassName="active">
                    Create Topic
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink className="btn btn-outline-primary mx-1 mb-1" to="/existing-topics" activeClassName="active">
                    Existing Topics
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink className="btn btn-outline-primary mx-1 mb-1" to="/claim-rewards" activeClassName="active">
                    Claim Rewards
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink className="btn btn-outline-primary mx-1 mb-1" to="/statistics" activeClassName="active">
                    Statistics
                  </NavLink>
                </li>
              </ul>
              <ul className="navbar-nav ms-auto">
                {account ? (
                  <li className="nav-item">
                    <div className="nav-link d-flex align-items-center">
                      <div ref={avatarRef} style={{ width: '30px', height: '30px' }} className="me-2"></div>
                      {account.slice(0, 6)}...{account.slice(-4)}
                    </div>
                  </li>
                ) : (
                  <li className="nav-item">
                    <button
                      onClick={handleConnectWallet}
                      className="btn btn-primary"
                    >
                      Connect Wallet
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </nav>


        <div className="container mt-5">
          <Routes>
            <Route path="/" element={<HubPage />} />
            <Route path="/assign-roles" element={<AssignRolesPage contract={contract} signerContract={signerContract} />} />
            <Route path="/create-topic" element={<CreateTopic contract={contract} signerContract={signerContract} />} />
            <Route path="/existing-topics" element={<ExistingTopics contract={contract} signerContract={signerContract} />} />
            <Route path="/claim-rewards" element={<ClaimRewards contract={contract} signerContract={signerContract} />} />
            <Route path="/statistics" element={<StatisticsPage contract={contract} signerContract={signerContract} />} />
          </Routes>
        </div>
      </Router>
      {/* Modal for Web3 Wallet Detection */}
      {showProviderModal && (
        <div>
          {/* Semi-transparent backdrop */}
          <div className="modal-backdrop show" style={{ opacity: 0.5 }}></div>

          {/* Modal dialog */}
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Web3 Wallet Required</h5>
                  <button type="button" className="btn-close" onClick={() => setShowProviderModal(false)} aria-label="Close"></button>
                </div>
                <div className="modal-body">
                  <p>No Web3 wallet detected. Please install MetaMask or another Web3-compatible wallet to interact with this DApp.</p>
                  <p>
                    <a href="https://metamask.io/download.html" target="_blank" rel="noopener noreferrer" className="btn btn-primary">Download MetaMask</a>
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowProviderModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Network Mismatch Detection */}
      {showNetworkModal && (
        <div>
          {/* Semi-transparent backdrop */}
          <div className="modal-backdrop show" style={{ opacity: 0.5 }}></div>

          {/* Modal dialog */}
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Network Mismatch</h5>
                  <button type="button" className="btn-close" onClick={() => setShowNetworkModal(false)} aria-label="Close"></button>
                </div>
                <div className="modal-body">
                  <p>The currently selected network does not match the DApp's network (6Degree).</p>
                  <p>Would you like to switch to the 6Degree network?</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-primary" onClick={handleSwitchNetwork}>Switch Network</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowNetworkModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
